import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Text } from '@/components/ui/text';
import { APIError } from '@/lib/api/api';
import { useAuth } from '@/lib/auth/auth';
import { useEmailVerificationResend } from '@/features/account/useEmailVerificationResend';
import { useUpdateEmailAddress } from '@/features/account/useUpdateEmailAddress';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { View } from 'react-native';

type EmailVerificationNoticeProps = {
  className?: string;
};

export function EmailVerificationNotice({ className }: EmailVerificationNoticeProps) {
  const session = useAuth((state) => state.session);
  const resendVerificationEmail = useEmailVerificationResend();
  const updateEmailAddress = useUpdateEmailAddress();
  const [emailUpdateOpen, setEmailUpdateOpen] = React.useState(false);
  const [emailDraft, setEmailDraft] = React.useState('');
  const [emailUpdateError, setEmailUpdateError] = React.useState<string | null>(null);
  const [freshLinkSent, setFreshLinkSent] = React.useState(false);

  const email = session?.me.user.email?.trim() ?? '';
  const needsEmailVerification = Boolean(session && session.me.email_verified === false);
  const emailDeliveryFailed = Boolean(session?.me.email_delivery_failed);

  React.useEffect(() => {
    if (!email) {
      return;
    }

    setEmailDraft(email);
    setEmailUpdateError(null);
    setFreshLinkSent(false);
  }, [email]);

  if (!needsEmailVerification) {
    return null;
  }

  const title = emailDeliveryFailed
    ? `We couldn't deliver email to ${email || 'this address'}.`
    : freshLinkSent
      ? `We sent a fresh verification link to ${email || 'your email'}.`
      : `Check ${email || 'your email'} to verify your account.`;

  const body = emailDeliveryFailed
    ? 'That address may be incorrect. Update it now and we will send a fresh verification link right away.'
    : freshLinkSent
      ? 'If that email is wrong, update it now so you do not get stuck trying to sign in with the wrong address later.'
      : 'Make sure this is the right email on your account. If it is wrong, update it now. If it is right, resend your verification link.';

  return (
    <>
      <View className={cn('rounded-[20px] border border-border bg-surface p-4', className)}>
        <Text className="text-[15px] font-semibold leading-5 text-foreground">{title}</Text>
        <Text className="mt-1.5 text-[13px] leading-5 text-muted-foreground">{body}</Text>

        <View className="mt-3 rounded-[14px] border border-border/70 bg-surface-alt px-3 py-2.5">
          <Text className="text-[11px] font-semibold uppercase tracking-[1.1px] text-muted-foreground">
            Email on file
          </Text>
          <Text className="mt-1 text-[14px] font-semibold text-foreground">
            {email || 'No email found'}
          </Text>
        </View>

        <View className="mt-3 gap-2 sm:flex-row">
          <Button
            size="sm"
            className="flex-1"
            onPress={() => {
              setEmailUpdateError(null);
              setEmailDraft(email);
              setEmailUpdateOpen(true);
            }}>
            <Text>Update email</Text>
          </Button>

          {!emailDeliveryFailed ? (
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={resendVerificationEmail.isPending || freshLinkSent}
              onPress={() => {
                resendVerificationEmail
                  .mutateAsync()
                  .then(() => setFreshLinkSent(true))
                  .catch(() => undefined);
              }}>
              <Text>
                {resendVerificationEmail.isPending
                  ? 'Sending...'
                  : freshLinkSent
                    ? 'Link sent'
                    : 'Resend link'}
              </Text>
            </Button>
          ) : null}
        </View>
      </View>

      <Dialog
        open={emailUpdateOpen}
        onOpenChange={(open) => {
          setEmailUpdateOpen(open);
          if (!open) {
            setEmailUpdateError(null);
            setEmailDraft(email);
          }
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update your email</DialogTitle>
            <DialogDescription>
              Your account is currently using {email || 'this email address'}. If that is not right,
              change it below and we will send a fresh verification link to the new address.
            </DialogDescription>
          </DialogHeader>

          <View className="gap-2">
            <Label nativeID="email-update-input">New email</Label>
            <Input
              aria-labelledby="email-update-input"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect={false}
              keyboardType="email-address"
              placeholder="you@example.com"
              value={emailDraft}
              onChangeText={(value) => {
                setEmailDraft(value);
                if (emailUpdateError) {
                  setEmailUpdateError(null);
                }
              }}
            />
          </View>

          {emailUpdateError ? (
            <Text variant="small" className="text-destructive">
              {emailUpdateError}
            </Text>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => {
                setEmailUpdateOpen(false);
                setEmailUpdateError(null);
                setEmailDraft(email);
              }}>
              <Text>Cancel</Text>
            </Button>
            <Button
              disabled={updateEmailAddress.isPending}
              onPress={() => {
                const nextEmail = emailDraft.trim().toLowerCase();
                if (!nextEmail) {
                  setEmailUpdateError('Enter the email address you want on this account.');
                  return;
                }

                updateEmailAddress
                  .mutateAsync(nextEmail)
                  .then((result) => {
                    setFreshLinkSent(result.verification_email_sent);
                    if (result.verification_email_sent) {
                      setEmailUpdateError(null);
                      setEmailUpdateOpen(false);
                      return;
                    }

                    setEmailUpdateError(
                      'We updated your email, but the verification link did not send yet. Try again in a moment.'
                    );
                  })
                  .catch((updateError) => {
                    if (updateError instanceof APIError && updateError.status === 409) {
                      setEmailUpdateError(
                        'That email is already connected to another account. Try signing in with it or use a different email.'
                      );
                      return;
                    }

                    if (updateError instanceof APIError && updateError.status === 400) {
                      setEmailUpdateError('Enter a valid email address and try again.');
                      return;
                    }

                    setEmailUpdateError(
                      updateError instanceof Error
                        ? updateError.message
                        : 'Unable to update your email right now.'
                    );
                  });
              }}>
              <Text>{updateEmailAddress.isPending ? 'Saving...' : 'Save and resend'}</Text>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
